import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { 
  Home, 
  Hammer, 
  Palette, 
  User, 
  Shield, 
  Handshake, 
  Video, 
  Mail, 
  BookOpen, 
  FileText, 
  Cookie, 
  Eye,
  Settings,
  ChevronRight,
  Globe,
  Search,
  Star,
  Clock,
  ArrowRight,
  Zap,
  TrendingUp,
  Map,
  Activity,
  BarChart3,
  Navigation
} from "lucide-react";
import { motion } from "framer-motion";

interface SitemapSection {
  title: string;
  description: string;
  icon: any;
  color: string;
  gradient: string;
  pages: {
    name: string;
    path: string;
    description: string;
    isNew?: boolean;
    isPro?: boolean;
    status?: 'live' | 'upcoming' | 'maintenance';
  }[];
}

const Sitemap = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Comprehensive sitemap data with all pages and sections
  const sitemapData: SitemapSection[] = [
    {
      title: "Core Pages",
      description: "Main website navigation and essential pages",
      icon: Home,
      color: "emerald",
      gradient: "from-emerald-500/20 via-emerald-600/20 to-teal-500/20",
      pages: [
        {
          name: "Home",
          path: "/",
          description: "Welcome page with featured auctions, live streams, and Florida Highwaymen heritage showcase",
          status: 'live'
        },
        {
          name: "Browse Artworks",
          path: "/artworks",
          description: "Comprehensive gallery of Florida Highwaymen paintings with advanced filtering and search",
          status: 'live'
        },
        {
          name: "Artwork Details",
          path: "/artworks/:id",
          description: "Individual artwork pages with high-resolution images, provenance, and bidding history",
          status: 'live'
        }
      ]
    },
    {
      title: "Auction Platform",
      description: "Live bidding, auction management, and real-time streaming",
      icon: Hammer,
      color: "blue",
      gradient: "from-blue-500/20 via-blue-600/20 to-indigo-500/20",
      pages: [
        {
          name: "All Auctions",
          path: "/auctions",
          description: "Complete auction listing with live, upcoming, and past auctions with advanced filters",
          status: 'live'
        },
        {
          name: "Auction Details",
          path: "/auctions/:id",
          description: "Individual auction pages with lot information, bidding interface, and live streaming",
          status: 'live'
        },
        {
          name: "Live Broadcast",
          path: "/broadcast",
          description: "Real-time auction streaming with interactive chat and live bidding capabilities",
          status: 'live',
          isNew: true
        }
      ]
    },
    {
      title: "User Dashboard",
      description: "Personal account management and bidding history",
      icon: User,
      color: "purple",
      gradient: "from-purple-500/20 via-purple-600/20 to-violet-500/20",
      pages: [
        {
          name: "Login / Register",
          path: "/login",
          description: "Secure authentication portal with instant access and social login options",
          status: 'live'
        },
        {
          name: "User Dashboard",
          path: "/dashboard",
          description: "Personal dashboard with bidding history, watchlist, saved items, and account overview",
          status: 'live'
        },
        {
          name: "Profile Settings",
          path: "/profile",
          description: "Enhanced account management with completion tracking, stats, and personal information",
          status: 'live',
          isNew: true
        },
        {
          name: "Dashboard Settings",
          path: "/dashboard/settings",
          description: "Advanced user preferences, privacy controls, and dashboard customization",
          status: 'live'
        }
      ]
    },
    {
      title: "Services",
      description: "Professional services and expert consultations",
      icon: Star,
      color: "orange",
      gradient: "from-orange-500/20 via-orange-600/20 to-amber-500/20",
      pages: [
        {
          name: "Art Appraisal",
          path: "/appraisal",
          description: "Professional artwork valuation services by certified Florida Highwaymen experts",
          status: 'live',
          isPro: true
        },
        {
          name: "Consignment Services",
          path: "/consign",
          description: "Consign your Florida Highwaymen artwork through our premium auction platform",
          status: 'live',
          isPro: true
        }
      ]
    },
    {
      title: "Information Hub",
      description: "Educational content and company information",
      icon: BookOpen,
      color: "green",
      gradient: "from-green-500/20 via-green-600/20 to-emerald-500/20",
      pages: [
        {
          name: "Blog & Articles",
          path: "/blog",
          description: "Educational content about Florida Highwaymen artists, techniques, and art history",
          status: 'live'
        },
        {
          name: "Blog Post",
          path: "/blog/:slug",
          description: "Individual blog articles with expert insights and detailed artwork analysis",
          status: 'live'
        },
        {
          name: "Contact Us",
          path: "/contact",
          description: "Get in touch with our team for inquiries, support, and expert consultations",
          status: 'live'
        },
        {
          name: "FAQs",
          path: "/faqs",
          description: "Frequently asked questions about Florida Highwaymen art and our auction process",
          status: 'live'
        }
      ]
    },
    {
      title: "Administration",
      description: "Platform management and administrative tools",
      icon: Shield,
      color: "red",
      gradient: "from-red-500/20 via-red-600/20 to-rose-500/20",
      pages: [
        {
          name: "Admin Dashboard",
          path: "/admin",
          description: "Comprehensive administrative interface for auction management and user oversight",
          status: 'live',
          isPro: true
        }
      ]
    },
    {
      title: "Legal & Privacy",
      description: "Terms, policies, and legal documentation",
      icon: FileText,
      color: "slate",
      gradient: "from-slate-500/20 via-slate-600/20 to-gray-500/20",
      pages: [
        {
          name: "Privacy Policy",
          path: "/privacy",
          description: "Comprehensive privacy policy with GDPR compliance and data protection details",
          status: 'live',
          isNew: true
        },
        {
          name: "Terms of Service",
          path: "/terms",
          description: "Detailed terms and conditions with auction-specific rules and fee structure",
          status: 'live',
          isNew: true
        },
        {
          name: "Cookie Policy",
          path: "/cookies",
          description: "Interactive cookie management with detailed explanations and user controls",
          status: 'live',
          isNew: true
        }
      ]
    }
  ];

  // Filter pages based on search term
  const filteredSections = sitemapData.map(section => ({
    ...section,
    pages: section.pages.filter(page => 
      page.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      page.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      page.path.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.pages.length > 0);

  // Count total pages
  const totalPages = sitemapData.reduce((total, section) => total + section.pages.length, 0);
  const livePages = sitemapData.reduce((total, section) => 
    total + section.pages.filter(page => page.status === 'live').length, 0
  );
  const newPages = sitemapData.reduce((total, section) => 
    total + section.pages.filter(page => page.isNew).length, 0
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative">
        {/* Background lighting effects */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            className="absolute top-20 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"
            initial={{ scale: 1, opacity: 0.3 }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="absolute bottom-20 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
            initial={{ scale: 1, opacity: 0.3 }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: 2,
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="absolute top-1/2 left-1/3 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"
            initial={{ scale: 1, opacity: 0.2 }}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              delay: 1,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="container max-w-7xl py-12 relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center px-8 py-3 mb-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl shadow-lg">
              <Map className="h-5 w-5 mr-3 text-emerald-400" />
              <span className="text-white font-light tracking-wide">Website Navigation</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-4">Site Map</h1>
            <p className="text-white/70 text-xl font-light max-w-3xl mx-auto mb-8">
              Discover every corner of our Florida Highwaymen auction platform. 
              Navigate with confidence through our comprehensive site structure.
            </p>
          </motion.div>

          {/* Stats Overview */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12"
          >
            <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl text-center">
              <CardContent className="p-6">
                <div className="mx-auto w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-emerald-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{totalPages}</div>
                <p className="text-white/60 text-sm">Total Pages</p>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl text-center">
              <CardContent className="p-6">
                <div className="mx-auto w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Activity className="h-6 w-6 text-green-400" />
                </div>
                <div className="text-3xl font-bold text-green-400 mb-2">{livePages}</div>
                <p className="text-white/60 text-sm">Live Pages</p>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl text-center">
              <CardContent className="p-6">
                <div className="mx-auto w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-blue-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{sitemapData.length}</div>
                <p className="text-white/60 text-sm">Categories</p>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl text-center">
              <CardContent className="p-6">
                <div className="mx-auto w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-orange-400" />
                </div>
                <div className="text-3xl font-bold text-orange-400 mb-2">{newPages}</div>
                <p className="text-white/60 text-sm">New Features</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mx-auto mb-12"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search pages, features, or content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-6 py-4 text-lg bg-white/10 backdrop-blur-xl border-white/20 text-white placeholder-white/60 focus:border-emerald-400/50 focus:ring-emerald-400/20 rounded-xl"
              />
            </div>
          </motion.div>

          {/* Quick Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Navigation className="h-6 w-6 text-emerald-400" />
              Quick Navigation
            </h2>
            <div className="flex flex-wrap gap-3">
              {sitemapData.map((section, index) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Button
                    variant={activeSection === section.title ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveSection(activeSection === section.title ? null : section.title)}
                    className={`transition-all duration-300 hover:scale-105 ${
                      activeSection === section.title 
                        ? `bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg border-emerald-500` 
                        : 'bg-white/10 backdrop-blur-xl border-white/20 text-white hover:bg-white/20 hover:border-white/40'
                    }`}
                  >
                    <section.icon className="w-4 h-4 mr-2" />
                    {section.title}
                  </Button>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Sitemap Sections */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-12"
          >
            {filteredSections.map((section, sectionIndex) => (
              <motion.div 
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: sectionIndex * 0.1 }}
                className={`transition-all duration-500 ${
                  activeSection && activeSection !== section.title ? 'opacity-30 scale-95' : 'opacity-100 scale-100'
                }`}
              >
                <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-500">
                  {/* Section Header */}
                  <CardHeader className={`bg-gradient-to-r ${section.gradient} backdrop-blur-xl border-b border-white/10 relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-white/5"></div>
                    <div className="relative z-10 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                          <section.icon className="h-8 w-8 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-2xl font-bold text-white">{section.title}</CardTitle>
                          <CardDescription className="text-white/90 text-lg mt-1">
                            {section.description}
                          </CardDescription>
                        </div>
                      </div>
                      <Badge className="bg-white/20 text-white border-white/30 px-3 py-1 backdrop-blur-sm">
                        {section.pages.length} page{section.pages.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </CardHeader>

                  {/* Pages Grid */}
                  <CardContent className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {section.pages.map((page, pageIndex) => (
                        <motion.div
                          key={page.path}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: pageIndex * 0.1 }}
                        >
                          <Link 
                            to={page.path.includes(':') ? '#' : page.path}
                            className="block group/page"
                          >
                            <Card className="bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/30 transition-all duration-300 hover:shadow-xl hover:scale-105 group-hover/page:shadow-2xl h-full">
                              <CardContent className="p-6 h-full flex flex-col">
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                      <h3 className="font-bold text-lg text-white group-hover/page:text-emerald-300 transition-colors">
                                        {page.name}
                                      </h3>
                                      <div className="flex gap-2">
                                        {page.status === 'live' && (
                                          <Badge className="bg-green-500/20 text-green-300 border-green-400/20 px-2 py-1 text-xs">
                                            <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                                            Live
                                          </Badge>
                                        )}
                                        {page.isNew && (
                                          <Badge className="bg-blue-500/20 text-blue-300 border-blue-400/20 px-2 py-1 text-xs">
                                            <Zap className="w-3 h-3 mr-1" />
                                            New
                                          </Badge>
                                        )}
                                        {page.isPro && (
                                          <Badge className="bg-purple-500/20 text-purple-300 border-purple-400/20 px-2 py-1 text-xs">
                                            <Star className="w-3 h-3 mr-1" />
                                            Pro
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <code className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-400/20 px-2 py-1 rounded font-mono mb-3 block">
                                      {page.path}
                                    </code>
                                    
                                    <p className="text-white/70 leading-relaxed text-sm flex-1">
                                      {page.description}
                                    </p>
                                  </div>
                                  
                                  <ArrowRight className="h-5 w-5 text-white/60 group-hover/page:text-emerald-400 transition-all group-hover/page:translate-x-1 flex-shrink-0 ml-4" />
                                </div>
                              </CardContent>
                            </Card>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Search Results Info */}
          {searchTerm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-12 text-center"
            >
              <p className="text-white/70">
                Found {filteredSections.reduce((total, section) => total + section.pages.length, 0)} page(s) 
                matching "{searchTerm}"
              </p>
            </motion.div>
          )}

          {/* No Results */}
          {searchTerm && filteredSections.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-12 text-center"
            >
              <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl max-w-md mx-auto">
                <CardContent className="p-8 text-center">
                  <Search className="h-16 w-16 text-white/40 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No results found</h3>
                  <p className="text-white/70">
                    No pages match your search term "{searchTerm}". Try a different keyword or browse our categories above.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-20"
          >
            <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Ready to Explore?
                </h2>
                <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                  Start your journey through our Florida Highwaymen auction platform. 
                  Discover authentic artwork, join live auctions, and become part of our community.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/auctions">
                    <Button 
                      size="lg" 
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 rounded-xl"
                    >
                      <Hammer className="mr-2 h-5 w-5" />
                      Browse Auctions
                    </Button>
                  </Link>
                  <Link to="/artworks">
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/40 px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 rounded-xl backdrop-blur-xl"
                    >
                      <Palette className="mr-2 h-5 w-5" />
                      View Artworks
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Sitemap; 